import {
  ButtonPrimary,
  Modal,
  TableContextProvider,
  TextAreaForm,
  useModalWizardStepsContext,
} from '@/components/new'
import { columns } from './columns'
import { PlusIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Switch } from '@/components/ui/switch'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { IDocumentTable } from '@/containers/logistic/expedients/wizzardExpedient/tabs/document/interfaces'
import UploadFile from '@/components/new/UploadFile'
import { useMutation, useQuery } from '@tanstack/react-query'
import { postMedia } from '@/api/mediaAPI'
import { toast } from 'react-toastify'
import { getFileDocuments } from '@/api/filesAPI'
import useDocumentIds from '@/state/useDocumentIds'
import { DataTable } from './DataTable'
import { IContainer, IDocument } from '../../../interfaces/interfaces'
import { TrashIcon } from '@heroicons/react/24/outline'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import DirectDownload from './DirectDownload'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import Button from '@/components/misc/Button'
import { 
  getContainerDocuments, 
  postContainerDocuments, 
  patchContainerDocuments, 
  deleteContainerDocuments 
} from '@/api/containersAPI'

export default function DocumentsContTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()

  const { useFormWizzard } = context
  const { setValue, control, watch } = useFormWizzard
  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const [showUploadFile, setShowUploadFile] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [dataTable, setDataTable] = useState<IDocument[]>([])
  const [selectedDocument, setSelectDocument] = useState({
    name: '',
    observations: '',
  })
  const [showDeleteDocument, setShowDeleteDocument] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<number>()

  // Query para obtener documentos en modo edición
  const { data: containerDocuments, refetch: refetchDocuments } = useQuery({
    queryKey: ['container-documents', editContainer?.id],
    queryFn: () => getContainerDocuments({ id: Number(editContainer!.id) }),
    enabled: !!isEditing && !!editContainer?.id,
  })

  // Inicializar dataTable según el modo
  useEffect(() => {
    if (isEditing && containerDocuments) {
      // En modo edición, usar documentos del backend
      setDataTable(containerDocuments.map((item: any) => ({
        id: item.id,
        name: item.name,
        path: item.path,
        observations: item.observations,
        actions: {
          remove: true,
          download: true,
        },
      })))
    } else if (!isEditing) {
      // En modo creación, usar documentos del estado local
      setDataTable(data.documents.map((item, index) => ({
        id: index,
        name: `Documento ${index + 1}`,
        path: item.path,
        observations: item.observations,
        actions: {
          remove: true,
          download: true,
        },
      })))
    }
  }, [isEditing, containerDocuments, data.documents])

  useEffect(() => {
    setSelectDocument({
      ...selectedDocument,
      observations: watch('observations'),
    })
  }, [watch('observations')])

  const sendMedia = useMutation({
    mutationFn: postMedia,
    onSuccess: (key) => {
      setIsSending(false)
      setShowUploadFile(false)
      
      const newDocument = {
        name: selectedDocument.name,
        path: key,
        observations: selectedDocument.observations,
        actions: {
          remove: true,
          download: true,
        },
      }

      if (isEditing && editContainer) {
        // En modo edición, crear documento directamente en el backend
        createContainerDocument.mutate({
          id: Number(editContainer.id),
          params: {
            name: selectedDocument.name,
            path: key,
            observations: selectedDocument.observations,
          }
        })
      } else {
        // En modo creación, guardar en estado local
        setSelectDocument({
          name: '',
          observations: '',
        })
        setValue('observations', '')
        toast.success('Documento subido con éxito')
        setDataTable((prev) => [
          ...prev,
          {
            ...newDocument,
            id: prev.length,
          },
        ])
        updateData({
          ...data,
          documents: [
            ...data.documents,
            {
              path: key,
              observations: selectedDocument.observations,
            },
          ],
        })
      }
    },
    onError: (error) => {
      setIsSending(false)
      toast.error(
        error.message ?? 'Ocurrió un error intentando subir el archivo'
      )
    },
  })

  // Mutaciones para documentos de contenedor
  const createContainerDocument = useMutation({
    mutationFn: postContainerDocuments,
    onSuccess: () => {
      toast.success('Documento creado con éxito')
      refetchDocuments()
      setSelectDocument({
        name: '',
        observations: '',
      })
      setValue('observations', '')
    },
    onError: (error) => {
      toast.error('Error al crear el documento')
    },
  })

  const deleteContainerDocument = useMutation({
    mutationFn: deleteContainerDocuments,
    onSuccess: () => {
      toast.success('Documento eliminado con éxito')
      refetchDocuments()
      setShowDeleteDocument(false)
    },
    onError: (error) => {
      toast.error('Error al eliminar el documento')
    },
  })

  // Función para exponer el guardado a la ventana (para el botón de guardar)
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveDocuments = () => {
        // En modo edición, no hay nada específico que guardar aquí
        // Los documentos se guardan individualmente al subirlos
        toast.success('Documentos guardados')
      }
      // @ts-ignore - Expose loading state
      window.isSavingDocuments = false
    }
  }, [isEditing])

  return (
    <div className={'h-[60dvh]'}>
      <TableContextProvider
        data={dataTable}
        isLoading={false}
        leftButtons={[
          {
            type: 'text',
            label: 'Listado de documentos',
          },
        ]}
        rightButtons={[
          {
            type: 'button',
            icon: <PlusIcon strokeWidth={2} className="h-5 w-5 text-black" />,
            label: 'Adjuntar documento',
            onClick: () => setShowUploadFile(true),
          },
        ]}
      >
        <DataTable
          columns={[
            ...columns,
            {
              accessorKey: 'actions',
              header: 'Acciones',
              cell: ({ row }) => {
                return (
                  <div className={'flex justify-center'}>
                    <DirectDownload path={row.original.path} />
                    <div
                      role={'button'}
                      className={`p-2`}
                      onClick={() => {
                        setShowDeleteDocument(true)
                        setDocumentToDelete(row.original.id)
                      }}
                    >
                      <TrashIcon className={`h-5 w-5 text-red-700`} />
                    </div>
                  </div>
                )
              },
            },
          ]}
          storageKey={'DocumentsCostTab'}
        />
      </TableContextProvider>

      {showUploadFile && (
        <Modal
          size={'s'}
          isOpen={!!showUploadFile}
          onClose={() => {
            setShowUploadFile(false)
            setSelectDocument({
              name: '',
              observations: '',
            })
            setValue('observations', '')
          }}
          showDialogAlert={false}
        >
          <div className="mx-6" style={{ marginBottom: '-1rem' }}>
            <TextAreaForm
              label={'Observaciones'}
              name={'observations'}
              placeholder={'Observaciones'}
              control={control}
              disabled={isSending}
            />
          </div>
          <UploadFile
            acceptedFileTypes={['pdf', 'images', 'word', 'excel']}
            loading={isSending}
            onUpload={(file) => {
          
              setIsSending(true)
              setSelectDocument({
                name: file.name,
                observations: selectedDocument.observations,
              })
              sendMedia.mutate({
                params: {
                  documentType: 'IMAGE',
                  size: 3,
                  ext: 'png',
                  isPrivate: true,
                  name: 'Pepe',
                },
                file,
              })
            }}
          />
        </Modal>
      )}
      <AlertDialog open={showDeleteDocument}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Si elimina ahora, perderá todos los datos asociados a este
              documento. ¿Está seguro de que desea eliminar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDocument(false)}>
              Cancelar
            </AlertDialogCancel>
            <Button
              name={'Sí, eliminar'}
              loading={deleteContainerDocument.isPending}
              disabled={deleteContainerDocument.isPending}
              action={() => {
                if (isEditing) {
                  deleteContainerDocument.mutate(documentToDelete as number)
                } else {
                  setDataTable(
                    dataTable.filter(
                      (item) => item.id !== (documentToDelete as number)
                    )
                  )
                  updateData({
                    ...data,
                    documents: data.documents.filter(
                      (_, index) => index !== (documentToDelete as number)
                    ),
                  })
                  setShowDeleteDocument(false)
                  toast.success('El documento se ha eliminado con éxito')
                }
              }}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
